import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Input } from 'reactstrap';
import * as actions from '../../actions';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import InputGroup from '../Forms/InputGroup';
import approve from 'approvejs';
import { getTranslationKey } from '../../modules/utils/helpers';
import type { Notify } from '../../typings/redux';
import styled from 'styled-components';

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 0 !important;
`;

interface FieldConfig {
  type: string;
  placeholder: string;
  tooltip_id: string;
  tooltip_text: string;
  title: string;
  rules: {
    required?: { message: string };
  };
}

interface FieldsConfig {
  description: FieldConfig;
  url: FieldConfig;
  privacy: FieldConfig;
}

const getFields = (): FieldsConfig => ({
  description: {
    type: 'text',
    placeholder: getTranslationKey('description'),
    tooltip_id: 'imprintDescription',
    tooltip_text: getTranslationKey('customDomains.descriptionTooltip'),
    title: getTranslationKey('description'),
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  },
  url: {
    type: 'text',
    placeholder: getTranslationKey('customDomains.url'),
    tooltip_id: 'urlToolTipPrivacy',
    tooltip_text: getTranslationKey('customDomains.urlToolTipPrivacy'),
    title: getTranslationKey('customDomains.url'),
    rules: {}
  },
  privacy: {
    type: 'textarea',
    placeholder: getTranslationKey('customDomains.privacy'),
    tooltip_id: 'privacyTooltip',
    tooltip_text: getTranslationKey('customDomains.privacyTooltip'),
    title: getTranslationKey('customDomains.privacy'),
    rules: {}
  }
});

interface PrivacyItem {
  _id: string;
  description: string;
  url?: string;
  privacy?: string;
}

interface PrivacysState {
  all: PrivacyItem[];
}

interface PrivacyFormValues {
  description: string;
  url?: string;
  privacy?: string;
}

interface PrivacyOwnProps {
  mode?: 'edit';
  selectedPrivacy?: string;
  onSelectedPrivacyChanged?: (privacyId: string) => void;
  domain?: string;
  privacys: PrivacysState;
  notify: Notify;
  createPrivacy: (values: PrivacyFormValues, notify: Notify) => void;
  editPrivacy: (values: PrivacyFormValues & { _id: string }, notify: Notify) => void;
  deletePrivacy: (id: string, notify: Notify) => void;
  updateDomain: (domain: { _id: string; privacy: string }) => void;
}

type PrivacyProps = PrivacyOwnProps & InjectedFormProps<PrivacyFormValues, PrivacyOwnProps>;

interface PrivacyState {
  isOpen: boolean;
  selectedPrivacy: string;
  currentPrivacyId?: string;
}

class Privacy extends Component<PrivacyProps, PrivacyState> {
  constructor(props: PrivacyProps) {
    super(props);

    this.state = {
      isOpen: false,
      selectedPrivacy: props.selectedPrivacy || 'select'
    };

    this.toggle = this.toggle.bind(this);
    this.submit = this.submit.bind(this);
    this.onSelectPrivacy = this.onSelectPrivacy.bind(this);
    this.delete = this.delete.bind(this);
  }

  onSelectPrivacy({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    if (this.props.mode === 'edit') {
      const currentPrivacy = this.props.privacys.all.filter((privacy) => privacy._id === value)[0];
      if (currentPrivacy) {
        this.setState({ currentPrivacyId: value });
        this.props.change('description', currentPrivacy.description);
        this.props.change('url', currentPrivacy.url);
        this.props.change('privacy', currentPrivacy.privacy);
        this.toggle();
      }
    } else {
      if (value === 'new') {
        this.toggle();
      } else {
        this.setState({ selectedPrivacy: value }, () => {
          const { onSelectedPrivacyChanged, domain } = this.props;
          if (onSelectedPrivacyChanged) {
            onSelectedPrivacyChanged(this.state.selectedPrivacy);
          }
          if (domain) {
            this.props.updateDomain({
              _id: domain,
              privacy: this.state.selectedPrivacy
            });
          }
        });
      }
    }
  }

  toggle() {
    this.setState(
      (prevState) => ({ isOpen: !prevState.isOpen }),
      () => {
        if (!this.state.isOpen) {
          this.props.reset();
        }
      }
    );
  }

  submit(values: PrivacyFormValues) {
    if (this.props.mode === 'edit') {
      this.props.editPrivacy({ ...values, _id: this.state.currentPrivacyId! }, this.props.notify);
    } else {
      this.props.createPrivacy(values, this.props.notify);
    }
    this.toggle();
  }

  delete() {
    this.props.deletePrivacy(this.state.currentPrivacyId!, this.props.notify);
    this.toggle();
  }

  render() {
    const privacys = this.props.privacys.all;
    const { mode } = this.props;
    const label =
      mode === 'edit'
        ? getTranslationKey('customDomains.editPrivacy')
        : getTranslationKey('customDomains.createPrivacy');

    return (
      <div className="inline">
        <StyledFormGroup>
          <div>
            <Input
              type="select"
              name="product"
              value={this.state.selectedPrivacy}
              onChange={this.onSelectPrivacy}
            >
              {mode === 'edit' && (
                <option value="select" disabled>
                  {getTranslationKey('customDomains.editPrivacy')}
                </option>
              )}
              {!mode && (
                <option value="select" disabled>
                  {getTranslationKey('pleaseSelect')}
                </option>
              )}
              {privacys.map((privacy, key) => (
                <option value={privacy._id} key={key}>
                  {privacy.description}
                </option>
              ))}
            </Input>
          </div>
        </StyledFormGroup>
        <Modal
          centered
          isOpen={this.state.isOpen}
          toggle={this.toggle}
          wrapClassName="modal-primary"
          size="default"
        >
          <ModalHeader toggle={this.toggle}>{label}</ModalHeader>
          <ModalBody>
            <div className="row">
              <div className="col-lg-12">
                <form onSubmit={this.props.handleSubmit(this.submit)}>
                  {Object.keys(getFields()).map((fieldKey, index) => (
                    <Field
                      component={InputGroup}
                      key={index}
                      name={fieldKey}
                      field={getFields()[fieldKey as keyof FieldsConfig]}
                    />
                  ))}
                  <button
                    className="btn btn-primary w-100-p"
                    type="submit"
                    disabled={this.props.pristine || this.props.invalid || this.props.submitting}
                  >
                    {label}
                  </button>
                </form>
                <a
                  href="https://dsgvo-muster-datenschutzerklaerung.dg-datenschutz.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getTranslationKey('customDomains.privacyGenerator')}
                </a>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            {mode === 'edit' && (
              <small className="cursor-pointer " onClick={this.delete}>
                <i className="sli-trash m-r-5" />
                {`${getTranslationKey('privacy')} ${getTranslationKey('delete')}`}
              </small>
            )}
            <button className="btn btn-danger pull-left" onClick={this.toggle}>
              {getTranslationKey('abort')}
            </button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

function validate(values: PrivacyFormValues) {
  const errors: any = {};

  if (!values.url && !values.privacy) {
    const error = [getTranslationKey('minOneRequired')];
    errors.privacy = error;
    errors.url = error;
  }

  Object.keys(getFields()).forEach((fieldKey) => {
    const result = approve.value(
      values[fieldKey as keyof PrivacyFormValues],
      getFields()[fieldKey as keyof FieldsConfig].rules
    );
    if (result.errors.length > 0) {
      errors[fieldKey] = result.errors;
    }
  });

  return errors;
}

interface RootState {
  privacys: PrivacysState;
  notify: Notify;
}

function mapStateToProps({ privacys, notify }: RootState) {
  return { privacys, notify };
}

const PrivacyWithForm = reduxForm<PrivacyFormValues, PrivacyOwnProps>({
  form: 'privacyCreationForm',
  validate
})(Privacy);

export default connect(mapStateToProps, actions)(PrivacyWithForm);
